import { Component, computed, effect, inject, Renderer2, signal } from "@angular/core"
import { RouterModule } from "@angular/router"
import { FormsModule } from "@angular/forms"
import { PageToolbarComponent } from "./components/page-toolbar.component"
import { MyAccountComponent } from "./components/my-account/my-account.component"
import { ContentModerationRowsComponent } from "./components/content/moderation/content-moderation-rows.component"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import { ProfileService } from "./components/user-profile/profile.service"
import { RouteService } from "./services/route.service"
import { EditorLoadService } from "./components/editor/services/editor-load.service"
import { AutoFocusDirective } from "@shared"
import { UiInheritDirective } from "ui-inherit"
import { ContentManagerComponent } from "./components/content/manager/content-manager.component"
import { NextSectionButtonComponent } from "./components/shared/next-section-button.component"
import { FirebaseAuthService } from "./auth/firebase-auth.service"
import { TourEditItemsComponent } from "./components/tour/tour-edit-items.component"
import { TourService } from "./components/tour/tour.service"
import { WA_LOCAL_STORAGE } from "@ng-web-apis/common"
import { regionsData } from "redlineLibs"

@Component({
  imports: [
    RouterModule,
    PageToolbarComponent,
    MyAccountComponent,
    ContentModerationRowsComponent,
    FormsModule,
    UiInheritDirective,
    ContentManagerComponent,
    AutoFocusDirective,
    NextSectionButtonComponent,
    NextSectionButtonComponent,
    TourEditItemsComponent,
  ],
  standalone: true,
  template: `
    @if (userInit()) {
      @if (!loggedIn() && firewall()) {
        <div class="flex justify-center items-center w-full h-screen space-x-2">
          <div>
            <input
              class="input input-bordered"
              libAutoFocus
              [type]="passwordType()"
              [ngModel]="password()"
              (ngModelChange)="inputEvent($event)"
              placeholder='super secret password'
            >
          </div>
          <button
            class="btn btn-sm secondary"
            (click)="passwordVisible.set(!passwordVisible())"
          >
            submit
          </button>
        </div>
      }
    }

    @if (loggedIn() || !firewall()) {

      <!-- 
      the page-toolbar element overrides the theme, so don't add components under it, 
      instead add in this app.component using @if 
      -->
      <header role="banner">
        <e2e-page-toolbar/>
      </header>

      <main id="main-content">
        <router-outlet/>
      </main>

      @if (timelineSection()) {
        <button routerLink="/stories-map" style="width: 100%; background: #f3f3f3; position: fixed; bottom: 0">
          <e2e-next-section-button/>
        </button>
      }

      <!--      <e2e-tour inherit/>-->
      @defer (when isAdmin()) {
        @if (showTourEditor()) {
          <e2e-tour-edit-items
            [activeIndexInput]="[currentItemIndex()]"
            (closeTourEditor)="setShowTourEditor(false)"
          />
        }
      }


      @defer (when isEditor()) {
        @if (isEditor() && contentManager_dialog() && section().MANAGER) {
          <e2e-content-manager inherit/>
        }
      }

      @defer (when isModerator()) {
        @if (contentModeration_dialog() && section().MODERATION) {
          <e2e-content-moderation-rows inherit/>
        }
      }

      @if (showMyAccount()) {
        <e2e-my-account inherit/>
      }

    }

    <!--    <lib-theme-switcher [enable]="true"/>-->
    <!--    <lib-ui-toast></lib-ui-toast>-->

    <!--
        <lib-ui-dialog [open]="showActiveReview_dialog().content" (close)="cancelActiveReview()">
          <div ngProjectAs="title">
            Please Confirm
          </div>
          <div ngProjectAs="content">
            <div class="column" style="align-items: center">
              <div>
               {{ showActiveReview_dialog().message }}
              </div>
              <div class="row">
                <button
                  class="btn btn-sm"
                  (click)="cancelActiveReview()"
                >
                  Cancel
                </button>
                <button
                  class="btn btn-sm"
                  (click)="startActiveReview(showActiveReview_dialog().content)"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </lib-ui-dialog>
    -->

  `,
  selector: "e2e-root",
})
export class AppComponent {
  private localStorage = inject(WA_LOCAL_STORAGE)
  private profileService = inject(ProfileService)
  private routeService = inject(RouteService)
  private editorLoadService = inject(EditorLoadService)
  private renderer = inject(Renderer2)
  private firebaseAuthService= inject(FirebaseAuthService)
  private tourService = inject(TourService)

  contentManager_dialog = this.routeService.contentManager_dialog
  contentModeration_dialog = this.routeService.contentModeration_dialog
  timelineSection = computed(() => this.routeService.sectionFlags().TIMELINE)

  showIsPublished_dialog = this.editorLoadService.showIsPublished_dialog
  routeHasPageToolbar = this.routeService.routeHasPageToolbar
  showActiveReview_dialog = this.editorLoadService.showActiveReview_dialog

  section = this.routeService.sectionFlags
  loggedIn = this.firebaseAuthService.loggedIn
  userInit = this.firebaseAuthService.userInit
  isAdmin = this.profileService.isAdmin
  isEditor = this.profileService.isEditor
  isModerator = this.profileService.isModerator

  password = signal<string | undefined>(undefined)
  passwordVisible = signal(false)
  passwordIcon = computed(() => (this.passwordVisible() ? faEye : faEyeSlash))
  passwordType = computed(() => (this.passwordVisible() ? "text" : "password"))
  firewallAuth = signal(false)
  firewall = computed(() => {
    return regionsData[this.routeService.region()].firewall &&
    !this.firewallAuth()
  })

  showMyAccount = computed(() =>
    this.section().LOGIN || this.section().PROFILE || this.section().MY_CONTENT
  )

  showTourEditor = this.tourService.showTourEditor
  currentItemIndex = this.tourService.currentItemIndex

/*
  themes = [
    "redline_light",
    "redline_dark",
    "light",
    "dark",
    "cupcake",
    "bumblebee",
    "emerald",
    "corporate",
    "synthwave",
    "retro",
    "cyberpunk",
    "valentine",
    "halloween",
    "garden",
    "forest",
    "aqua",
    "lofi",
    "pastel",
    "fantasy",
    "wireframe",
    "black",
    "luxury",
    "dracula",
    "cmyk",
    "autumn",
    "business",
    "acid",
    "lemonade",
    "night",
    "coffee",
    "winter",
    "dim",
    "nord",
    "sunset",
  ]
*/

/*
  icons = [
    faSun,
    faMoon,
    faSun,
    faMoon,
  ]
*/


  constructor() {
    // this.themeService.setThemeNames(this.themes)
    // this.themeService.setThemeIcons(this.icons)

    this.firewallAuth.set(!!this.localStorage.getItem("accessKey"))
    effect(() => {
      if (
        this.routeService.sectionFlags().TIMELINE &&
        this.routeService.fragments()[0] !== "view" &&
        this.routeService.fragments()[0] !== "edit"
      ) {
        this.renderer.setStyle(document.body, "user-select", "none")
      } else {
        this.renderer.removeStyle(document.body, "user-select")
      }
    })
  }

  inputEvent(event: string): void {
    if (event.toLowerCase() === "supersecretpassword" || event === "ssp") {
      this.localStorage.setItem("accessKey", "true")
      this.firewallAuth.set(true)
    }
  }

  hideIsPublished_dialog() {
    this.editorLoadService.hideIsPublished_dialog()
  }

  setShowTourEditor(value: boolean) {
    this.tourService.setShowTourEditor(value)
  }

}
